import React from "react";
import styled from "styled-components";
import { Col, Row, Form } from "react-bootstrap";
import { PageContainer } from "../common/CommonStyles";
import { useTranslation } from "react-i18next";
import { useLiveData } from "../common/utils";
import {
  Account,
  AccountContext,
  editProfilePath,
} from "../common/context/AccountContext";
import {
  AnalyticsContext,
  MenuAddProfileButtonTapEvent,
  MenuLessonRecordingsButtonTapEvent,
  MenuLogInButtonTapEvent,
  MenuLogOutButtonTapEvent,
  MenuProfileSettingsButtonTapEvent,
  MenuSendFeedbackButtonTapEvent,
  MenuUpgradeButtonTapEvent,
  ViewSettingsEvent,
} from "../common/context/AnalyticsContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { Link, useHistory } from "react-router-dom";
import {
  Profile,
  isInTrialMode,
  isSchoolUser,
  isSchoolUserStudent,
  isSchoolUserTeacher,
  isUserSignedUp,
} from "../common/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import {
  faUserCircle,
  faChevronRight,
  faUserPlus,
  faFileLines,
  faEnvelope,
  faFilm,
  faCircleExclamation,
  faCheck,
  faGear,
  faPaperPlane,
  faStarCircle,
  faUser,
  faRightFromBracket,
} from "@fortawesome/pro-solid-svg-icons";
import { ApiContext } from "../common/context/ApiContext";
import { Alert } from "@mui/material";

const enum SaveSettingStatus {
  NONE,
  SAVING,
  SAVE_SUCCESS,
  SAVE_ERROR,
}

// Bordered group of rows, divided by hairlines.
const SettingsGroup = styled.div.attrs({
  className: "border rounded overflow-hidden mt-2 mb-4",
})`
  > * + * {
    border-top: var(--bs-border-width) solid var(--bs-border-color);
  }
`;

const SettingsRowBody = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem 0.5rem 0.75rem;
  width: 100%;
  border: none;
  background: none;
  color: inherit;
  text-align: start;
  text-decoration: none;
`;

// One settings row: icon, label, and a chevron or a trailing control. It's a
// link when `to` is set, a button when only `onClick` is set.
const SettingsRow: React.FC<{
  icon: IconDefinition;
  label: string;
  to?: string;
  onClick?: () => void;
  trailing?: React.ReactNode;
  hideChevron?: boolean;
}> = ({ icon, label, to, onClick, trailing, hideChevron }) => {
  const content = (
    <>
      <FontAwesomeIcon icon={icon} fixedWidth />
      <span style={{ flexGrow: 1 }}>{label}</span>
      {trailing ??
        (!hideChevron && (to || onClick) && (
          <FontAwesomeIcon icon={faChevronRight} />
        ))}
    </>
  );
  if (to) {
    return (
      <SettingsRowBody as={Link} to={to} onClick={onClick}>
        {content}
      </SettingsRowBody>
    );
  }
  if (onClick) {
    return (
      <SettingsRowBody as="button" onClick={onClick}>
        {content}
      </SettingsRowBody>
    );
  }
  return <SettingsRowBody>{content}</SettingsRowBody>;
};

// Settings hub, opened from the sidebar gear. Non-school users reach it
// through the grown-ups hurdle; school users get a shorter layout.
export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setCurrentTab } = React.useContext(TabsContext);
  const { accountLiveData, updateUser, logout } =
    React.useContext(AccountContext);
  const [account, setAccount] = React.useState<Account>();
  const { modifyUser } = React.useContext(ApiContext);
  const [profiles, setProfiles] = React.useState<Profile[]>([]);
  const [userCanCreateProfile, setUserCanCreateProfile] =
    React.useState<boolean>(false);
  const [recordLessonReplays, setRecordLessonReplays] =
    React.useState<boolean>();
  const [receivesPostLessonEmails, setReceivesPostLessonEmails] =
    React.useState<boolean>();
  const { logEvent } = React.useContext(AnalyticsContext);
  const [saveSettingStatus, setSaveSettingStatus] =
    React.useState<SaveSettingStatus>(SaveSettingStatus.NONE);

  const handleRecordLessonReplaysCheckboxChange = async (event) => {
    const newValue = event.target.checked;
    setRecordLessonReplays(newValue);
    setSaveSettingStatus(SaveSettingStatus.SAVING);
    const updatedUser = await modifyUser(
      receivesPostLessonEmails!!,
      newValue,
      true
    );
    if (updatedUser !== undefined) {
      updateUser(updatedUser!!, false, null, false);
      setSaveSettingStatus(SaveSettingStatus.SAVE_SUCCESS);
    } else {
      setRecordLessonReplays(!newValue);
      setSaveSettingStatus(SaveSettingStatus.SAVE_ERROR);
    }
  };

  const handleReceivesPostLessonEmailsCheckboxChange = async (event) => {
    const newValue = event.target.checked;
    setReceivesPostLessonEmails(newValue);
    setSaveSettingStatus(SaveSettingStatus.SAVING);
    const updatedUser = await modifyUser(newValue, recordLessonReplays!!, true);
    if (updatedUser !== undefined) {
      updateUser(updatedUser!!, false, null, false);
      setSaveSettingStatus(SaveSettingStatus.SAVE_SUCCESS);
    } else {
      setReceivesPostLessonEmails(!newValue);
      setSaveSettingStatus(SaveSettingStatus.SAVE_ERROR);
    }
  };

  React.useEffect(() => {
    logEvent(new ViewSettingsEvent());
  }, [logEvent]);

  React.useEffect(() => {
    setCurrentTab(TabEnum.Settings);
  }, [setCurrentTab]);

  useLiveData(
    React.useCallback(
      (account: Account) => {
        if (account.hasUserAndMaybeProfile()) {
          setAccount(account);
          setRecordLessonReplays(account.user!!.record_lesson_replays);
          setReceivesPostLessonEmails(
            account.user!!.receives_post_lesson_emails
          );
          setProfiles(
            account.user!!.profiles.filter((profile) => {
              return profile.is_guest === false;
            })
          );
          setUserCanCreateProfile(account.canCreateProfile());
        }
      },
      [setProfiles]
    ),
    accountLiveData
  );

  if (!account) {
    return <></>;
  }

  const legalRows = (
    <SettingsGroup>
      <SettingsRow icon={faFileLines} label={t("settings_legal")} to="/legal/" />
    </SettingsGroup>
  );

  const logOutRows = isUserSignedUp(account.user) && (
    <SettingsGroup>
      <SettingsRow
        icon={faRightFromBracket}
        label={t("navigation_logout")}
        onClick={() => {
          logEvent(new MenuLogOutButtonTapEvent());
          logout();
          history.push("/");
        }}
        hideChevron
      />
    </SettingsGroup>
  );

  return (
    <PageContainer>
      {saveSettingStatus === SaveSettingStatus.SAVE_ERROR && (
        <Alert
          icon={
            <FontAwesomeIcon icon={faCircleExclamation} fontSize="inherit" />
          }
          severity="error"
          style={{ marginBottom: "15px" }}
          onClose={() => {
            setSaveSettingStatus(SaveSettingStatus.NONE);
          }}
        >
          {t("settings_update_error")}
        </Alert>
      )}
      {saveSettingStatus === SaveSettingStatus.SAVE_SUCCESS && (
        <Alert
          icon={<FontAwesomeIcon icon={faCheck} fontSize="inherit" />}
          severity="success"
          style={{ marginBottom: "15px" }}
          onClose={() => {
            setSaveSettingStatus(SaveSettingStatus.NONE);
          }}
        >
          {t("settings_update_success")}
        </Alert>
      )}
      <h2>
        {t(
          isSchoolUser(account.user) ? "navigation_settings" : "settings_title"
        )}
      </h2>
      <Row className="justify-content-center mt-4">
        <Col xs={12} lg={8} xl={6}>
          {isSchoolUser(account.user) ? (
            <>
              {/* Profile */}
              {account.profile?.is_guest === false && (
                <SettingsGroup>
                  <SettingsRow
                    icon={faUserCircle}
                    label={account.profile.first_name}
                    to={editProfilePath(account.profile.id)}
                    onClick={() =>
                      logEvent(new MenuProfileSettingsButtonTapEvent())
                    }
                  />
                </SettingsGroup>
              )}
              {isSchoolUserTeacher(account.user) && (
                <SettingsGroup>
                  <SettingsRow
                    icon={faPaperPlane}
                    label={t("send_feedback")}
                    to="/feedback/"
                    onClick={() =>
                      logEvent(new MenuSendFeedbackButtonTapEvent())
                    }
                  />
                </SettingsGroup>
              )}
              {!isSchoolUserStudent(account.user) && legalRows}
              {logOutRows}
            </>
          ) : (
            <>
              {/* Profiles */}
              {(profiles.length > 0 || userCanCreateProfile) && (
                <>
                  <h5 className="mt-2">{t("settings_profiles")}</h5>
                  <SettingsGroup>
                    {profiles.map((profile) => (
                      <SettingsRow
                        key={profile.id}
                        icon={faUserCircle}
                        label={profile.first_name}
                        to={editProfilePath(profile.id)}
                      />
                    ))}
                    {userCanCreateProfile && (
                      <SettingsRow
                        icon={faUserPlus}
                        label={t("settings_add_profile")}
                        to="/account/profile/add/"
                        onClick={() =>
                          logEvent(new MenuAddProfileButtonTapEvent())
                        }
                        hideChevron
                      />
                    )}
                  </SettingsGroup>
                </>
              )}

              {/* User setting switches */}
              {isUserSignedUp(account.user) && (
                <SettingsGroup>
                  <SettingsRow
                    icon={faEnvelope}
                    label={t("settings_receive_post_lesson_emails")}
                    trailing={
                      <Form>
                        <Form.Check
                          type="checkbox"
                          id="receive-post-lesson-emails"
                          label=""
                          checked={receivesPostLessonEmails === true}
                          onChange={
                            handleReceivesPostLessonEmailsCheckboxChange
                          }
                          disabled={
                            saveSettingStatus === SaveSettingStatus.SAVING
                          }
                        />
                      </Form>
                    }
                  />
                  <SettingsRow
                    icon={faFilm}
                    label={t("settings_record_lesson_replays")}
                    trailing={
                      <Form>
                        <Form.Check
                          type="checkbox"
                          id="record-lesson-replays"
                          label=""
                          checked={recordLessonReplays === true}
                          onChange={handleRecordLessonReplaysCheckboxChange}
                          disabled={
                            saveSettingStatus === SaveSettingStatus.SAVING
                          }
                        />
                      </Form>
                    }
                  />
                </SettingsGroup>
              )}

              {/* Account */}
              <SettingsGroup>
                {(!isUserSignedUp(account.user) ||
                  isInTrialMode(account.user)) && (
                  <SettingsRow
                    icon={faStarCircle}
                    label={t("navigation_upgrade")}
                    to="/account/upgrade/"
                    onClick={() => logEvent(new MenuUpgradeButtonTapEvent())}
                  />
                )}
                {isUserSignedUp(account.user) ? (
                  <SettingsRow
                    icon={faGear}
                    label={t("settings_manage_account")}
                    to="/account/manage/"
                  />
                ) : (
                  <SettingsRow
                    icon={faUser}
                    label={t("navigation_login")}
                    to="/account/login/"
                    onClick={() => logEvent(new MenuLogInButtonTapEvent())}
                  />
                )}
                <SettingsRow
                  icon={faFilm}
                  label={t("navigation_lesson_replays")}
                  to="/account/lesson-replays/"
                  onClick={() =>
                    logEvent(new MenuLessonRecordingsButtonTapEvent())
                  }
                />
              </SettingsGroup>

              {legalRows}

              {/* Feedback */}
              <SettingsGroup>
                <SettingsRow
                  icon={faPaperPlane}
                  label={t("send_feedback")}
                  to="/feedback/"
                  onClick={() =>
                    logEvent(new MenuSendFeedbackButtonTapEvent())
                  }
                />
              </SettingsGroup>

              {logOutRows}
            </>
          )}
        </Col>
      </Row>
    </PageContainer>
  );
};
